import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Server, Upload, RefreshCw, AlertTriangle } from "lucide-react";

export function AdminIspInventory() {
  const [stats, setStats] = useState(null);
  const [text, setText] = useState("");
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/isp-inventory"); setStats(data); }
    catch (e) { logError(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) { toast.error("Paste at least one ISP line"); return; }
    if (mode === "replace" && !window.confirm(`Replace ALL inventory with these ${lines.length} ISPs? Any currently-issued IPs will be wiped.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/isp-inventory", { proxies: lines, mode });
      setStats(data.stats);
      setText("");
      toast.success(`${mode === "replace" ? "Replaced" : `Added ${data.added}`} — inventory now ${data.stats.total} ISPs`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally { setBusy(false); }
  };

  const total = stats?.total ?? 0;
  const held = stats?.held ?? 0;
  const available = stats?.available ?? 0;
  const fresh = stats?.fresh ?? 0;
  const recycled = stats?.recycled ?? 0;
  const soldRecent = stats?.sold_24h ?? 0;
  const pct = total ? Math.round((held / total) * 100) : 0;
  const lowStock = available < 25;

  return (
    <div className="bg-white border border-[#E4EEEC] rounded-2xl overflow-hidden shadow-sm" data-testid="admin-isp-inventory">
      <div className="flex items-center justify-between p-4 border-b border-[#EFF6F5]">
        <h3 className="font-head text-lg font-semibold text-ink flex items-center gap-2"><Server className="w-4 h-4 text-neon" aria-hidden="true" /> ISP Inventory</h3>
        <button onClick={load} className="text-sm text-zinc-500 hover:text-neon flex items-center gap-1" data-testid="isp-inventory-refresh-button"><RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Refresh</button>
      </div>

      <div className="p-5 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["Total ISPs", total, "text-ink"],
            ["Available", available, "text-neon"],
            ["Sold", held, "text-ink"],
            ["Recycled (freed)", recycled, "text-ink"],
          ].map(([label, val, color]) => (
            <div key={label} className="bg-base border border-[#EFF6F5] rounded-xl p-4" data-testid={`isp-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/,"")}`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">{label}</div>
              <div className={`font-head text-2xl font-extrabold ${color}`}>{val}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs font-mono text-zinc-500">
          <span data-testid="isp-stat-fresh">Fresh (virgin): <span className="text-ink font-semibold">{fresh}</span></span>
          <span data-testid="isp-stat-sold-24h">Sold (last 24h): <span className="text-ink font-semibold">{soldRecent}</span></span>
        </div>

        <div>
          <div className="flex justify-between text-xs text-zinc-500 mb-1.5 font-mono">
            <span>Sold — {held} / {total}</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-[#EFF6F5] overflow-hidden">
            <div className="h-full bg-neon transition-all" style={{ width: `${pct}%` }} />
          </div>
          {lowStock && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> Low stock — only {available} IPs left. Top up soon so new subscriptions can be sold.</p>
          )}
          {stats?.next_up_ips?.length > 0 && (
            <p className="text-xs text-zinc-400 mt-2 font-mono">Next up (fresh first): {stats.next_up_ips.join(", ")} …</p>
          )}
          {stats?.recently_freed_ips?.length > 0 && (
            <p className="text-xs text-zinc-400 mt-1 font-mono" data-testid="isp-recently-freed">Recently freed: {stats.recently_freed_ips.join(", ")} …</p>
          )}
        </div>

        <div className="border-t border-[#EFF6F5] pt-5">
          <div className="flex items-center gap-2 mb-2">
            <Upload className="w-4 h-4 text-neon" aria-hidden="true" />
            <span className="font-medium text-ink text-sm">Add / replace ISPs</span>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="isp-upload-textarea"
            rows={5}
            placeholder={"Paste one proxy per line:\nip:port:username:password"}
            className="w-full bg-base border border-[#E4EEEC] focus:border-neon rounded-xl p-3 font-mono text-xs text-ink outline-none"
          />
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="isp-upload-mode" className="h-11 px-3 bg-white border border-[#E4EEEC] focus:border-neon rounded-xl text-sm text-ink">
              <option value="append">Append (add to stock)</option>
              <option value="replace">Replace all</option>
            </select>
            <Button onClick={upload} disabled={busy || !text.trim()} data-testid="isp-upload-button" className="h-11 bg-neon text-white hover:bg-neon-hover font-semibold rounded-xl">
              {busy ? "Uploading…" : "Upload ISPs"}
            </Button>
            <span className="text-xs text-zinc-400">Duplicates are skipped automatically on append.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
